import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  ChevronRight,
  Shield,
  Globe,
  Wrench,
  Truck,
  CheckCircle,
  Calculator,
  Images,
} from "lucide-react";

const HomePage = () => {
  // SEO title
  useEffect(() => {
    document.title = "Fast&Car | Importation de Véhicules depuis l'Europe et la Corée du Sud";
  }, []);

  const trustIndicators = [
    {
      icon: <Wrench className="w-6 h-6" />,
      title: "Technicien Diplômé",
      description: "Expertise technique certifiée",
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Import Europe & Corée",
      description: "Réseau international",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Inspection Complète",
      description: "Vérification multi-points",
    },
    {
      icon: <Truck className="w-6 h-6" />,
      title: "Livraison Nationale",
      description: "Partout en France",
    },
  ];

  const advantages = [
    "Recherche personnalisée selon vos critères",
    "Négociation par un expert",
    "Inspection mécanique approfondie",
    "Accompagnement administratif complet",
    "Rapatriement et livraison",
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-32 md:pt-40">
        {/* Background - Dark gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-900 to-black" />
        
        {/* Corvette Image - Isolated vehicle */}
        <div
          className="absolute inset-0 bg-contain bg-center bg-no-repeat md:bg-contain"
          style={{
            backgroundImage: `url('/assets/corvette-new.avif')`,
            backgroundPosition: 'center 60%',
            backgroundSize: 'cover',
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        {/* Carbon texture overlay */}
        <div 
          className="absolute inset-0 opacity-20 pointer-events-none mix-blend-overlay" 
          style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}} 
        />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-12 py-12 md:py-24">
          <div className="max-w-3xl">
            <p
              className="font-mono text-neon-lime text-xs md:text-sm tracking-widest uppercase mb-4 md:mb-6 animate-fade-up"
              data-testid="hero-subtitle"
            >
              Mandataire Automobile
            </p>
            <h1
              className="font-unbounded leading-tight mb-4 md:mb-6 animate-fade-up delay-100"
              data-testid="hero-title"
            >
              <span className="text-white text-2xl md:text-4xl lg:text-5xl block">Votre prochain véhicule est peut-être à l'étranger.</span>
              <span className="text-neon-lime text-glow text-xl md:text-3xl lg:text-4xl block mt-2">Nous allons le chercher pour vous.</span>
            </h1>
            <p
              className="font-manrope text-base md:text-xl text-zinc-300 leading-relaxed mb-6 md:mb-8 max-w-xl animate-fade-up delay-200"
              data-testid="hero-description"
            >
              Europe & Corée du Sud. Recherche personnalisée, inspection experte
              et livraison clé en main par un technicien diplômé.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-up delay-300">
              <Link to="/services">
                <Button
                  data-testid="hero-cta-primary"
                  className="bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider px-8 py-6 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)] text-base"
                >
                  <span>Découvrir les offres</span>
                </Button>
              </Link>
              <Link to="/process">
                <Button
                  data-testid="hero-cta-secondary"
                  variant="outline"
                  className="border-zinc-700 text-white font-unbounded font-bold uppercase tracking-wider px-8 py-6 hover:bg-zinc-800 text-base"
                >
                  <span>Comment ça marche</span>
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-zinc-600 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-neon-lime rounded-full" />
          </div>
        </div>
      </section>

      {/* Pourquoi Importer Section */}
      <section className="py-16 md:py-24 bg-zinc-950 relative">
        {/* Carbon texture */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none" 
          style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}} 
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-12">
            <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4">
              Avantages
            </p>
            <h2 className="font-unbounded text-3xl md:text-4xl font-bold text-white mb-4">
              Pourquoi <span className="text-neon-lime">importer</span> son véhicule ?
            </h2>
            <p className="font-manrope text-zinc-400 text-lg max-w-2xl mx-auto">
              L'importation vous ouvre les portes d'un marché plus large, avec des prix souvent plus attractifs et un choix incomparable.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Tarifs */}
            <div className="bg-zinc-900/50 border border-zinc-800 p-6 md:p-8 hover:border-neon-lime/30 transition-colors group">
              <div className="w-12 h-12 border border-zinc-700 flex items-center justify-center mb-4 group-hover:border-neon-lime transition-colors">
                <span className="text-2xl">💰</span>
              </div>
              <h3 className="font-unbounded text-xl font-bold text-white mb-3">
                Des tarifs plus compétitifs
              </h3>
              <p className="text-zinc-400 font-manrope leading-relaxed">
                En Allemagne ou en Corée du Sud, les véhicules premium sont souvent <strong className="text-white">15 à 30% moins chers</strong> qu'en France. Même avec les frais d'import, vous réalisez des économies significatives.
              </p>
            </div>
            
            {/* Choix */}
            <div className="bg-zinc-900/50 border border-zinc-800 p-6 md:p-8 hover:border-neon-lime/30 transition-colors group">
              <div className="w-12 h-12 border border-zinc-700 flex items-center justify-center mb-4 group-hover:border-neon-lime transition-colors">
                <span className="text-2xl">🌍</span>
              </div>
              <h3 className="font-unbounded text-xl font-bold text-white mb-3">
                Une variété inégalée
              </h3>
              <p className="text-zinc-400 font-manrope leading-relaxed">
                Accédez à des modèles <strong className="text-white">rares ou introuvables en France</strong> : finitions spécifiques, motorisations exclusives, options haut de gamme. Trouvez exactement ce que vous cherchez.
              </p>
            </div>
            
            {/* État */}
            <div className="bg-zinc-900/50 border border-zinc-800 p-6 md:p-8 hover:border-neon-lime/30 transition-colors group">
              <div className="w-12 h-12 border border-zinc-700 flex items-center justify-center mb-4 group-hover:border-neon-lime transition-colors">
                <span className="text-2xl">✨</span>
              </div>
              <h3 className="font-unbounded text-xl font-bold text-white mb-3">
                Un état exceptionnel
              </h3>
              <p className="text-zinc-400 font-manrope leading-relaxed">
                Les véhicules importés, notamment de Corée du Sud, bénéficient d'un <strong className="text-white">entretien méticuleux</strong> : kilométrages bas, carnet complet, stockage en garage. Une qualité souvent supérieure au marché français.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20 md:py-28 bg-carbon-paper border-y border-zinc-800 relative">
        {/* Carbon texture */}
        <div 
          className="absolute inset-0 opacity-15 pointer-events-none" 
          style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}} 
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            data-testid="trust-indicators"
          >
            {trustIndicators.map((item, index) => (
              <div
                key={index}
                data-testid={`trust-indicator-${index}`}
                className="text-center group"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 border border-zinc-700 text-neon-lime mb-4 group-hover:border-neon-lime group-hover:shadow-[0_0_20px_rgba(57,255,20,0.3)] transition-all duration-300">
                  {item.icon}
                </div>
                <h3 className="font-unbounded text-white font-bold text-sm md:text-base mb-2">
                  {item.title}
                </h3>
                <p className="text-zinc-500 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-24 md:py-32 relative">
        {/* Carbon texture */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none" 
          style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}} 
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="aspect-[4/5] bg-zinc-900 overflow-hidden border border-zinc-800">
                <img
                  src="https://images.unsplash.com/photo-1746079074494-822fb0f83364?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Inspection mécanique"
                  className="w-full h-full object-cover"
                  data-testid="founder-image"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-neon-lime/20 -z-10" />
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4">
                Votre Expert
              </p>
              <h2
                className="font-unbounded text-3xl md:text-5xl font-bold text-white mb-6"
                data-testid="founder-title"
              >
                L'Expertise avant
                <br />
                <span className="metallic-text">la Vente</span>
              </h2>
              <p className="font-manrope text-zinc-400 text-lg leading-relaxed mb-8">
                Mécanicien diplômé avec plusieurs années d'expérience en atelier
                et en dépannage sur le terrain, je mets à profit mes compétences
                techniques pour vous trouver le véhicule idéal.
              </p>
              <p className="font-manrope text-zinc-400 text-lg leading-relaxed mb-8">
                <span className="text-white font-semibold">Fast&Car</span> ne se
                contente pas de vendre des voitures. Nous les inspectons, les
                validons et les sécurisons pour vous garantir un achat serein.
              </p>

              <ul className="space-y-3 mb-8">
                {advantages.map((item, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-3 text-zinc-300"
                  >
                    <CheckCircle className="w-5 h-5 text-neon-lime flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <Link to="/contact">
                <Button
                  data-testid="founder-cta"
                  className="bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider px-8 py-4 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
                >
                  <span>Parlons de votre projet</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Import Origins */}
      <section className="py-24 md:py-32 bg-carbon-paper border-y border-zinc-800 relative">
        {/* Carbon texture */}
        <div 
          className="absolute inset-0 opacity-15 pointer-events-none" 
          style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}} 
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4">
              Nos Sources
            </p>
            <h2 className="font-unbounded text-3xl md:text-5xl font-bold text-white">
              Importation <span className="text-neon-lime">Internationale</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Europe */}
            <div
              className="bg-zinc-900/50 border border-zinc-800 p-8 md:p-10 hover:border-neon-lime/30 transition-colors group"
              data-testid="import-europe"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 border border-zinc-700 flex items-center justify-center group-hover:border-neon-lime transition-colors">
                  <span className="text-2xl">🇪🇺</span>
                </div>
                <h3 className="font-unbounded text-2xl font-bold text-white">
                  Europe
                </h3>
              </div>
              <p className="text-zinc-400 font-manrope leading-relaxed mb-6">
                Allemagne, Belgique, Italie, Espagne... Accédez aux meilleurs
                véhicules du marché européen avec nos 4 forfaits
                d'accompagnement adaptés à vos besoins.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-neon-lime font-bold uppercase tracking-wider text-sm hover:gap-4 transition-all"
              >
                <span>Voir les forfaits</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Corée du Sud */}
            <div
              className="bg-zinc-900/50 border border-zinc-800 p-8 md:p-10 hover:border-neon-lime/30 transition-colors group"
              data-testid="import-korea"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 border border-zinc-700 flex items-center justify-center group-hover:border-neon-lime transition-colors">
                  <span className="text-2xl">🇰🇷</span>
                </div>
                <h3 className="font-unbounded text-2xl font-bold text-white">
                  Corée du Sud
                </h3>
              </div>
              <p className="text-zinc-400 font-manrope leading-relaxed mb-4">
                <strong className="text-white">Européennes haut de gamme</strong> : BMW, Audi, Mercedes, Porsche... 
                À des prix plus compétitifs qu'en Europe, avec un état souvent exceptionnel et des kilométrages très bas.
              </p>
              <p className="text-zinc-500 text-sm mb-6">
                Les propriétaires coréens sont réputés pour leur méticulosité : entretien rigoureux, carnet complet, garage systématique.
              </p>
              <Link
                to="/simulateur"
                className="inline-flex items-center gap-2 text-neon-lime font-bold uppercase tracking-wider text-sm hover:gap-4 transition-all"
              >
                <span>Simuler un import</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-24 md:py-32 relative">
        {/* Carbon texture */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none" 
          style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}} 
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Gallery Card */}
            <Link to="/galerie" className="group">
              <div className="bg-zinc-900/50 border border-zinc-800 p-8 md:p-10 hover:border-neon-lime/30 transition-all duration-300 group-hover:-translate-y-2">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 border border-zinc-700 flex items-center justify-center group-hover:border-neon-lime transition-colors">
                    <Images className="w-6 h-6 text-neon-lime" />
                  </div>
                  <h3 className="font-unbounded text-2xl font-bold text-white">
                    Galerie
                  </h3>
                </div>
                <p className="text-zinc-400 font-manrope leading-relaxed mb-6">
                  Découvrez les véhicules que nous avons importés pour nos clients. 
                  Des berlines allemandes aux SUV coréens.
                </p>
                <span className="inline-flex items-center gap-2 text-neon-lime font-bold uppercase tracking-wider text-sm group-hover:gap-4 transition-all">
                  <span>Voir les réalisations</span>
                  <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </Link>

            {/* Calculator Card */}
            <Link to="/simulateur" className="group">
              <div className="bg-zinc-900/50 border border-zinc-800 p-8 md:p-10 hover:border-neon-lime/30 transition-all duration-300 group-hover:-translate-y-2">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 border border-zinc-700 flex items-center justify-center group-hover:border-neon-lime transition-colors">
                    <Calculator className="w-6 h-6 text-neon-lime" />
                  </div>
                  <h3 className="font-unbounded text-2xl font-bold text-white">
                    Simulateur
                  </h3>
                </div>
                <p className="text-zinc-400 font-manrope leading-relaxed mb-6">
                  Estimez le coût total de votre import : véhicule, forfait Fast&Car 
                  et carte grise avec malus CO2.
                </p>
                <span className="inline-flex items-center gap-2 text-neon-lime font-bold uppercase tracking-wider text-sm group-hover:gap-4 transition-all">
                  <span>Calculer mon budget</span>
                  <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-carbon-paper border-t border-zinc-800 relative">
        {/* Carbon texture */}
        <div 
          className="absolute inset-0 opacity-15 pointer-events-none" 
          style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}} 
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="relative bg-zinc-900 border border-zinc-800 p-12 md:p-20 text-center overflow-hidden">
            {/* Decorative corners */}
            <div className="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-neon-lime/50" />
            <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-neon-lime/50" />

            <h2
              className="font-unbounded text-3xl md:text-5xl font-bold text-white mb-6"
              data-testid="cta-title"
            >
              Prêt à trouver votre
              <br />
              <span className="text-neon-lime text-glow">véhicule idéal</span> ?
            </h2>
            <p className="font-manrope text-zinc-400 text-lg max-w-2xl mx-auto mb-10">
              Contactez-nous pour discuter de votre projet. Premier échange sans
              engagement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  data-testid="cta-contact-btn"
                  className="bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider px-6 sm:px-10 py-5 sm:py-6 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)] text-sm sm:text-base"
                >
                  <span className="hidden sm:inline">Demander un devis gratuit</span>
                  <span className="sm:hidden">Devis gratuit</span>
                </Button>
              </Link>
              <a href="tel:0768013271">
                <Button
                  data-testid="cta-phone-btn"
                  variant="outline"
                  className="border-zinc-700 text-white font-unbounded font-bold uppercase tracking-wider px-6 sm:px-10 py-5 sm:py-6 hover:bg-zinc-800 text-sm sm:text-base"
                >
                  <span>07 68 01 32 71</span>
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
